import { useState, useEffect, useRef } from "react";
import { Space, Table, Button, Input, Spin, Popconfirm } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type { InputRef } from "antd";
import type { FilterConfirmProps } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { Participant } from "../../models/Participant";
import "./Gallery.scss";
import { Toast } from "primereact/toast";
import CustomButton from "../../components/AccommodationData/CustomButton/CustomButton";
import CreateNewAlbumModal from "../../components/CreateNewAlbum/CreateNewAlbum";
import { deleteOneAlbums, getAllAlbums } from "../../services/GalleryService";
import { AxiosResponse } from "axios";
import { AlbumDto } from "../../models/Album";

const Gallery = () => {
  const { setBreadcrumb } = useApplicationContext();

  const toast = useRef<any>(null);
  const [albumList, setAlbumList] = useState<Participant[]>([]);
  const [selectedAlbums, setSelectedAlbums] = useState();

  const [openModal, setOpenModal] = useState(false);

  const [paginationNumber, setPaginationNumber] = useState<any>(30);
  const [pageNumber, setPageNumber] = useState<any>(1);

  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const [activeSpinner, setActiveSpinner] = useState<boolean>(false);

  const searchInput = useRef<InputRef>(null);

  const [indicatorRefresh, setIndicatorRefresh] = useState(false);

  useEffect(() => {
    setBreadcrumb(["Galerija", "Lista galerija"]);

    getAllAlbums().then((res: AxiosResponse) => {
      setAlbumList(res.data.result);
    });
  }, [indicatorRefresh]);

  type DataIndex = keyof Participant;
  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<Participant> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Pretraži`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, border: "none" }}
          >
            Pretraži
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              close();
              setSearchedColumn("");
            }}
            size="small"
            style={{ width: 90 }}
          >
            Poništi
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#C7363D" : undefined }} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex] ?? "")
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
            color: "#0D4077",
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const deleteOneAlbum = async (albumId: any, id: string) => {
    await deleteOneAlbums(albumId.id).then((res: any) => {
      setIndicatorRefresh(!indicatorRefresh);
    });
  };

  const columns: ColumnsType<Participant | any> = [
    {
      title: "Red. broj",
      width: 100,
      dataIndex: "firstName",
      key: "firstName",
      render: (firstName, text, index) => {
        return (
          <span className="blue-text">
            {(pageNumber - 1) * paginationNumber + index + 1}
          </span>
        );
      },
    },
    {
      title: "Album",
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => (a.title < b.title ? -1 : 1),
      //defaultSortOrder: "ascend",
      render: (title: any, record) => {
        return <span className="blue-text name-container">{title}</span>;
      },
      ...getColumnSearchProps("firstName"),
    },

    {
      title: "Događaj",
      dataIndex: "event",
      key: "event",
      sorter: (a, b) => (a.event < b.event ? -1 : 1),
      //defaultSortOrder: "ascend",
      render: (event: any, record) => {
        return (
          <span className="blue-text name-container">{record.event.name}</span>
        );
      },
    },

    {
      title: "Opcije",
      fixed: "right",
      width: 250,
      key: "action",
      align: "center",
      render: (e, record) => (
        <Space size="middle">
          <span
            className="action-button"
            onClick={() => {
              setSelectedAlbums(record);
              setOpenModal(!openModal);
            }}
          >
            Pogledaj
          </span>

          <Popconfirm
            title="Brisanje albuma?"
            description="Da li ste sigurni da želite da izbrišete album?"
            onConfirm={() => {
              deleteOneAlbum(e, record);
            }}
            onCancel={() => {}}
            okText="Da"
            cancelText="Ne"
          >
            <span className="action-button">Obrši</span>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const handleOpenModal = () => {
    setOpenModal(!openModal);
    setSelectedAlbums(undefined);
    setIndicatorRefresh(!indicatorRefresh);
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />

      <div className="participant-list">
        <Spin spinning={activeSpinner}>
          <div className="overview-legend-right">
            <div className="btn-container">
              <CustomButton
                danger={false}
                htmlType={"submit"}
                type={"primary"}
                function={() => handleOpenModal()}
                text={"Kreiraj novi album"}
              />
            </div>
          </div>

          <Table
            sticky={true}
            //scroll={{ x: 1500 }}
            columns={columns}
            rowKey={(data) => {
              return data.id;
            }}
            pagination={{
              current: pageNumber,
              onChange(current) {
                setPageNumber(current);
              },
              // defaultPageSize: 3,
              showSizeChanger: true,
              pageSizeOptions: ["30", "50", "100"],
            }}
            dataSource={albumList}
            onChange={(pagination, filters, sorter, extra) => {
              setPaginationNumber(pagination.pageSize);
            }}
            className="table-container"
            // rowSelection={{
            //   type: selectionType,
            //   ...rowSelection,
            // }}
          />
        </Spin>
        {openModal && (
          <CreateNewAlbumModal
            toast={toast}
            handleOpenModal={handleOpenModal}
            showModal={openModal}
            selectedAlbums={selectedAlbums}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default Gallery;
